// vendors
import React, { useState, useEffect } from "react";
import {
  Grid,
  Title,
  Card,
  Switch,
  Select,
  Loader,
  Text,
  Group,
  Image,
  ActionIcon,
} from "@mantine/core";
import { Line } from "@ant-design/plots";
import * as dayjs from "dayjs";
import MapGL, { Source, Layer } from "react-map-gl";
import { apiWithInterceptor } from "../../../../api";
import mapboxgl from "mapbox-gl";
import selectImage from "../../../../assets/undraw_selection_re_ycpo.svg";
import {
  clusterLayer,
  clusterCountLayer,
  unclusteredPointLayer,
} from "./layers";
import { IconArrowsMaximize, IconArrowsMinimize } from "@tabler/icons";
import heatmapLayer from "./map-styles";
import Charts from "./Charts";
mapboxgl.workerClass =
  require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default; // eslint-disable-line

export default function Dashboard() {
  const [ticketPerHour, setTicketPerHour] = useState([]);
  const [report, setReport] = useState([]);
  const [attendancePerHour, setAttendancePerHour] = useState([]);
  const [totalAttendance, setTotalAttendance] = useState(0);
  const [data, setData] = useState({});
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState();
  const [selectReportType, setReportType] = useState("NORMAL");
  const [totalAnswers, setTotalAnswers] = useState(0);
  const [ticketsPerDay, setTicketPerDay] = useState([]);
  const [attendancePerDay, setAttendancePerDay] = useState([]);
  const [allTicketDays, setAllTicketDays] = useState([]);
  const [allAttendanceDays, setAllAttendanceDays] = useState([]);
  const [dayCounterTicket, setDayCounterTicket] = useState(0);
  const [dayCounterAttendance, setDayCounterAttendance] = useState(0);
  const [mapType, setMapType] = useState(false);
  const [confirmedInfo, setConfirmedInfo] = useState("");
  const [loading, setLoading] = useState(true);
  const [allDaysTickets, setAllDaysTickets] = useState([]);
  const [allDaysAttendance, setAllDaysAttendance] = useState([]);
  const [height, setHeight] = useState("500px");
  const [mapHeight, setMapHeight] = useState("400px");

  const mapProps = {
    initialViewState: {
      latitude: 36.35133,
      longitude: 43.14718,
      zoom: 7,
    },
    mapStyle: "mapbox://styles/mapbox/dark-v9",
    mapboxAccessToken:
      "pk.eyJ1IjoibXVzdGFmYTk4IiwiYSI6ImNsYzdpaWRndzA0MW8zcXBjZ2Jnc2RqYmEifQ.OchSDOPK9Qv20J4_RSrK2w",
  };

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, [sessionId]);

  useEffect(() => {
    setMapHeight((prvHeight) => (prvHeight === "400px" ? "800px" : "400px"));
  }, [height]);

  useEffect(() => {
    apiWithInterceptor
      .get("/session")
      .then((res) => {
        setSessions(res?.data?.array);
        setSessionId(Object.values(res?.data?.array)[0].id);
        setHeight("500px");
        setMapHeight("400px");
      })
      .catch((err) => {});
  }, []);

  useEffect(() => {
    if (typeof sessionId !== "undefined") {
      apiWithInterceptor
        .get(`/session/report/ticket-registration/${sessionId}?type=TOTAL`)
        .then((res) => {
          Object.values(res?.data?.array_of_ticket_registered)?.forEach(
            (value) => {
              value.from = dayjs(value.at)
                .add(3, "hours")
                .format("YYYY/MM/DD HH:MM");
              delete value.at;
            }
          );
          setAllDaysTickets(res?.data?.array_of_ticket_registered);
          setTicketPerHour(res?.data?.array_of_ticket_registered);
          setDayCounterTicket(null);
        })
        .catch((err) => {
          if (err.response.status === 400 || err.response.status === 500)
            setTicketPerHour([]);
        });

      apiWithInterceptor
        .get(`/session/report/attendance-registration/${sessionId}?type=TOTAL`)
        .then((res) => {
          Object.values(res?.data?.array_of_attendance_registered)?.forEach(
            (value) => {
              value.from = dayjs(value.at)
                .add(3, "hours")
                .format("YYYY/MM/DD HH:MM");
              delete value.at;
            }
          );
          setAllDaysAttendance(res?.data?.array_of_attendance_registered);
          setAttendancePerHour(res?.data?.array_of_attendance_registered);
          setDayCounterAttendance(null);
        })
        .catch((err) => {
          if (err.response.status === 400 || err.response.status === 500)
            setAttendancePerHour([]);
        });
    }
  }, [sessionId]);

  useEffect(() => {
    if (typeof sessionId !== "undefined") {
      apiWithInterceptor
        .get(
          `/session/report/attendance-registration/${sessionId}?type=DayByDay`
        )
        .then((res) => {
          if (res?.data?.count !== 0) {
            setAttendancePerDay(res?.data);
            let allDays = [{ value: "TOTAL", label: "All days" }];
            Object.values(res?.data?.array_of_attendance_registered).map(
              (val) => {
                allDays.push({ value: val.date, label: val.date });
                val?.attendances?.forEach((value) => {
                  value.from = dayjs(value.at)
                    .add(3, "hours")
                    .format("YYYY/MM/DD HH:MM");
                  delete value.at;
                });
                setAttendancePerHour(val?.attendances);
                setDayCounterAttendance(val?.day_counter);
              }
            );
            setAllAttendanceDays(allDays);
            setTotalAttendance(res?.data?.count);
          } else {
            setAttendancePerHour([]);
            setDayCounterAttendance(0);
          }
        })
        .catch((err) => {
          if (err.response.status === 400 || err.response.status === 500)
            setAttendancePerHour([]);
          setAttendancePerHour([]);
          setDayCounterAttendance(0);
        });
    }
  }, [sessionId]);

  useEffect(() => {
    if (typeof sessionId !== "undefined") {
      apiWithInterceptor
        .get(`/session/report/ticket-registration/${sessionId}?type=DayByDay`)
        .then((res) => {
          if (res?.data?.count !== 0) {
            setTicketPerDay(res?.data);
            let allDays = [{ value: "TOTAL", label: "All days" }];
            Object.values(res?.data?.array_of_ticket_registered).map((val) => {
              allDays.push({ value: val.date, label: val.date });
              val?.tickets?.forEach((value) => {
                value.from = dayjs(value.at)
                  .add(3, "hours")
                  .format("YYYY/MM/DD HH:MM");
                delete value.at;
              });
              setTicketPerHour(val?.tickets);
              setDayCounterTicket(val?.day_counter);
            });
            setAllTicketDays(allDays);
            setTotalAnswers(res?.data?.count);
          } else {
            setTicketPerHour([]);
            setDayCounterTicket(0);
          }
        })
        .catch((err) => {
          if (err.response.status === 400 || err.response.status === 500)
            setTicketPerHour([]);
          setTicketPerHour([]);
          setDayCounterTicket(0);
        });
    }
  }, [sessionId]);

  useEffect(() => {
    if (typeof sessionId !== "undefined") {
      apiWithInterceptor
        .get(`/session/report/answers/${sessionId}?type=${selectReportType}`)
        .then((res) => {
          setReport(res.data);
        })
        .catch((error) => {
          console.log(error);
          setReport([]);
        });
    }
  }, [sessionId, selectReportType]);

  useEffect(() => {
    if (typeof sessionId !== "undefined") {
      apiWithInterceptor.get(`/ticket/${sessionId}/count`).then((res) => {
        setConfirmedInfo(res.data);
      });
    }
  }, [sessionId]);

  useEffect(() => {
    if (typeof sessionId !== "undefined") {
      apiWithInterceptor
        .get(`/ticket/${sessionId}/heat-map`)
        .then((res) => {
          let features = [];
          res?.data?.map((val, index) => {
            if (val.coordinates !== null && val.coordinates[0] !== null) {
              features = [
                ...features,
                {
                  type: "Feature",
                  properties: {
                    id: `akc${index * 3}`,
                  },
                  geometry: {
                    type: "Point",
                    // coordinates: val.coordinates.reverse(), // make sure to remove later (reverse)
                    coordinates: [val.coordinates[0], val.coordinates[1]],
                  },
                },
              ];
            }
            setData({
              type: "FeatureCollection",
              features: features,
            });
          });
          setMapType(false);
        })
        .catch((err) => {
          setData(null);
        });
    }
  }, [sessionId]);

  const allSessions = sessions?.map((item) => {
    return {
      value: item.id,
      label: item.name,
    };
  });

  const reportType = [
    { value: "NORMAL", label: "Tickets" },
    { value: "ATTENDANCE", label: "Attendance" },
    { value: "CONFIRMED", label: "Confirmed" },
    { value: "APPROVED", label: "Approved" },
    { value: "SENT_APPROVED", label: "Approved & Sent(Email/SMS)" },
  ];
  const lineChartOneConfig = {
    data: ticketPerHour,
    padding: "auto",
    xField: "from",
    yField: "tickets",
    color: "#ffa500",
    xAxis: {
      tickCount: 5,
    },

    slider: {
      start: 0,
      end: 1,
    },
  };

  const lineChartTwoConfig = {
    data: attendancePerHour,
    padding: "auto",
    xField: "from",
    yField: "tickets",
    color: "#ffa500",
    xAxis: {
      tickCount: 5,
    },

    slider: {
      start: 0,
      end: 1,
    },
  };

  return (
    <>
      <Grid>
        <Grid.Col
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
          lg={12}
          sm={12}
          xs={12}
          md={12}
        >
          <Select
            placeholder="Select the session to view it's report"
            label="Select session"
            data={allSessions}
            value={sessionId}
            searchable
            onChange={(val) => {
              setSessionId(val);
              setLoading(true);
              setReportType("NORMAL");
              setHeight("500px");
              setMapHeight("400px");
            }}
          />
        </Grid.Col>
      </Grid>
      {loading ? (
        <Grid>
          <Grid.Col
            xs={12}
            lg={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Loader color="yellow" size={100} />
          </Grid.Col>
        </Grid>
      ) : typeof sessionId !== "undefined" ? (
        <Grid>
          {ticketPerHour.length >= 1 ? (
            <>
              <Grid.Col
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                lg={4}
                sm={4}
                xs={4}
                md={4}
              >
                <Card withBorder style={{ height: "60px" }}>
                  <Text
                    sx={{
                      fontFamily: "Greycliff CF, sans-serif",
                      fontSize: "25px",
                      textAlign: "center",
                    }}
                    fw={700}
                  >
                    Confirmed Tickets : {confirmedInfo?.confirmed}
                  </Text>
                </Card>
              </Grid.Col>
              <Grid.Col
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                lg={4}
                sm={4}
                xs={4}
                md={4}
              >
                <Card withBorder style={{ height: "60px" }}>
                  <Text
                    sx={{
                      fontFamily: "Greycliff CF, sans-serif",
                      fontSize: "25px",
                      textAlign: "center",
                    }}
                    fw={700}
                  >
                    Not Confirmed :{" "}
                    {Math.abs(
                      confirmedInfo?.all_approved - confirmedInfo?.confirmed
                    )}
                  </Text>
                </Card>
              </Grid.Col>
              <Grid.Col
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                lg={4}
                sm={4}
                xs={4}
                md={4}
              >
                <Card withBorder style={{ height: "60px" }}>
                  <Text
                    sx={{
                      fontFamily: "Greycliff CF, sans-serif",
                      fontSize: "25px",
                      textAlign: "center",
                    }}
                    fw={700}
                  >
                    Total Approved : {confirmedInfo?.all_approved}
                  </Text>
                </Card>
              </Grid.Col>
              <Grid.Col
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                lg={12}
                sm={12}
                xs={12}
                md={12}
              >
                <Card shadow="sm" p="lg" radius="md" withBorder>
                  <Grid>
                    <Grid.Col
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                      }}
                      xs={12}
                    >
                      <Title
                        order={3}
                        size="h2"
                        style={{
                          marginBottom: "35px",
                        }}
                      >
                        Tickets/Hour{" "}
                        {dayCounterTicket !== null
                          ? `- Day Total (${dayCounterTicket})`
                          : null}
                        - All Days Total ({totalAnswers})
                      </Title>
                      <Group style={{ marginLeft: "50px", marginTop: "-25px" }}>
                        <Text>Select Day</Text>
                        <Select
                          placeholder="Select Day"
                          data={allTicketDays}
                          defaultValue="TOTAL"
                          onChange={(e) => {
                            if (e === "TOTAL") {
                              setTicketPerHour(allDaysTickets);
                              setDayCounterTicket(null);

                              console.log(allDaysTickets);
                            } else {
                              const currentDay =
                                ticketsPerDay.array_of_ticket_registered.find(
                                  (item) => item.date === e
                                );
                              setTicketPerHour(currentDay.tickets);

                              setDayCounterTicket(currentDay.day_counter);
                            }
                          }}
                        />
                      </Group>
                    </Grid.Col>

                    <Grid.Col xs={12}>
                      <Line {...lineChartOneConfig} />
                    </Grid.Col>
                  </Grid>
                </Card>
              </Grid.Col>
            </>
          ) : null}

          {attendancePerHour.length >= 1 ? (
            <Grid.Col
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
              lg={12}
              sm={12}
              xs={12}
              md={12}
            >
              <Card shadow="sm" p="lg" radius="md" withBorder>
                <Grid>
                  <Grid.Col
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                    }}
                    xs={12}
                  >
                    <Title
                      order={3}
                      size="h2"
                      style={{
                        marginBottom: "35px",
                      }}
                    >
                      Attendances/Hour
                      {dayCounterAttendance !== null
                        ? `- Day Total (${dayCounterAttendance})`
                        : null}{" "}
                      - All Days Total ({totalAttendance})
                    </Title>
                    <Group style={{ marginLeft: "50px", marginTop: "-25px" }}>
                      <Text>Select Day</Text>
                      <Select
                        placeholder="Select Day"
                        data={allAttendanceDays}
                        defaultValue="TOTAL"
                        onChange={(e) => {
                          if (e === "TOTAL") {
                            setAttendancePerHour(allDaysAttendance);
                            setDayCounterAttendance(null);
                          } else {
                            const currentDay =
                              attendancePerDay.array_of_attendance_registered.find(
                                (item) => item.date === e
                              );
                            setAttendancePerHour(currentDay.attendances);
                            setDayCounterAttendance(currentDay.day_counter);
                          }
                        }}
                      />
                    </Group>
                  </Grid.Col>

                  <Grid.Col xs={12}>
                    <Line {...lineChartTwoConfig} />
                  </Grid.Col>
                </Grid>
              </Card>
            </Grid.Col>
          ) : null}
          {report.length >= 1 ? (
            <>
              <Grid.Col
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                lg={12}
                sm={12}
                xs={12}
                md={12}
              >
                <Select
                  placeholder="Tickets - Attendance - Confirmed Tickets"
                  label="Display report by"
                  data={reportType}
                  searchable
                  value={selectReportType}
                  onChange={(val) => {
                    setReportType(val);
                    // setLoading(true);
                  }}
                />
              </Grid.Col>
              <Grid.Col
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  textAlign: "center",
                }}
                lg={12}
                sm={12}
                xs={12}
                md={12}
              >
                <h2>
                  Report By{" "}
                  {selectReportType.charAt(0) +
                    selectReportType.slice(1).toLowerCase()}{" "}
                  Tickets
                </h2>
              </Grid.Col>
            </>
          ) : null}

          {report.length >= 1
            ? report.map((item, index) => {
                return (
                  <Grid.Col
                    key={index}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                    lg={4}
                    sm={4}
                    xs={12}
                    md={4}
                  >
                    <Card shadow="sm" p="lg" radius="md" withBorder>
                      <Charts report={item.answers} label={item.label} />
                    </Card>
                  </Grid.Col>
                );
              })
            : null}
          <Grid.Col
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
            lg={12}
            sm={12}
            xs={12}
            md={12}
          >
            <Card
              shadow="sm"
              p="lg"
              radius="md"
              withBorder
              style={{ height: `${height + 100}` }}
            >
              {data !== null ? (
                <>
                  <Card.Section
                    p={10}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <ActionIcon
                      size="lg"
                      onClick={() => {
                        setHeight(height === "500px" ? "900px" : "500px");
                      }}
                    >
                      {height === "500px" ? (
                        <IconArrowsMaximize />
                      ) : (
                        <IconArrowsMinimize />
                      )}
                    </ActionIcon>

                    <Text style={{ marginRight: "10px" }}>
                      Select Map type Heatmap/Clustermap
                    </Text>
                    <Switch
                      onChange={(e) => setMapType(e.target.checked)}
                      onLabel="Heatmap"
                      offLabel="Clustermap"
                      size="xl"
                    />
                  </Card.Section>
                  <MapGL
                    {...mapProps}
                    key={`${mapHeight}-${mapType}`}
                    style={{ width: "100%", height: mapHeight }}
                  >
                    {mapType === false && (
                      <Source
                        id="cluster-map-data"
                        cluster={true}
                        clusterMaxZoom={14}
                        clusterRadius={50}
                        type="geojson"
                        data={data}
                      >
                        <Layer {...clusterLayer} />
                        <Layer {...clusterCountLayer} />
                        <Layer {...unclusteredPointLayer} />
                      </Source>
                    )}
                    {mapType === true && (
                      <Source id="heatmap-data" type="geojson" data={data}>
                        <Layer {...heatmapLayer} />
                      </Source>
                    )}
                  </MapGL>
                </>
              ) : (
                <h1>This session has no heatmap data</h1>
              )}
            </Card>
          </Grid.Col>
        </Grid>
      ) : (
        <Grid
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "100px",
          }}
        >
          <Grid.Col style={{ width: "300px", height: "200px" }} xs={12}>
            <Image
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
              src={selectImage}
              alt="Select session"
            />
          </Grid.Col>
          <Grid.Col xs={12}>
            <Title
              size="h3"
              order={3}
              style={{
                textDecoration: "underline",
                textDecorationColor: "#ffa500",
              }}
            >
              Select Session form the list
            </Title>
          </Grid.Col>
        </Grid>
      )}
    </>
  );
}
